/**
 * ProfileResumeFields — Resume editor for Account Settings > Profile > Resume tab.
 *
 * Mirrors ParsedResumeStep UX:
 *  - Section accordion list with icon + title + chevron
 *  - WorkExperienceCard for work-experience sections (structured form + rich-text description)
 *  - RichTextToolbar + MarkdownSectionEditor for every other section
 *  - "Add New Section" panel at bottom
 *
 * Data flow:
 *  Draft (structured) ──on mount──► ResumeSection[] (local state)
 *  ResumeSection[]   ──on change──► onPatch(structuredFields)
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

import {
  AlertCircle,
  Award,
  BookOpen,
  Briefcase,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Code,
  FileText,
  GraduationCap,
  Plus,
  Sparkles,
  Trash2,
  Upload,
  User,
  X,
} from 'lucide-react';

import LoadingSpinner from '../../../components/LoadingSpinner';
import { supabase } from '../../../lib/supabase';

import MarkdownSectionEditor from '../../dashboard/components/AIResumeOptimizer/steps/MarkdownSectionEditor';
import RichTextToolbar from '../../dashboard/components/AIResumeOptimizer/steps/RichTextToolbar';
import WorkExperienceCard from '../../dashboard/components/AIResumeOptimizer/steps/WorkExperienceCard';
import type { ResumeSection } from '../../dashboard/components/AIResumeOptimizer/utils/parseResumeSections';
import {
  getWorkExperienceDisplayContent,
  isWorkExperienceJsonContent,
  parseWorkExperienceContent,
  serializeWorkExperienceContent,
} from '../../dashboard/components/AIResumeOptimizer/utils/workExperienceFields';
import { langchainResumeParser } from '../../../services/langchainService';
import type {
  Certification,
  Education,
  Language,
  Project,
  WorkExperience,
} from '../../../types/userProfile';
import type { OnboardingDraft, OnboardingErrors } from '../types';
import {
  findSectionIndexForKey,
  getRecommendedSkills,
  getSectionAiTip,
  getSectionHealth,
  RESUME_SECTION_FOCUS_EVENT,
  skillsToContent,
  type ResumeSectionFocusDetail,
  type SectionHealthTone,
} from '../resumeAiHelpers';

const RESUME_FONT = 'var(--ds-font-sans)';

/** Canonical English section titles → i18n slug under views.accountSettings.resume.addSections.* */
const RESUME_SECTION_TITLE_KEYS: Record<string, string> = {
  'Work Experience': 'workExperience',
  Education: 'education',
  Skills: 'skills',
  Certifications: 'certifications',
  Languages: 'languages',
  Projects: 'projects',
  Awards: 'awards',
  'Professional Summary': 'professionalSummary',
  Goal: 'goal',
  Achievements: 'achievements',
  Publications: 'publications',
  Hobbies: 'hobbies',
  References: 'references',
};

const HEALTH_LABEL_KEYS: Record<string, string> = {
  Empty: 'empty',
  Strong: 'strong',
  Good: 'good',
  'Too Short': 'tooShort',
  'Missing Metrics': 'missingMetrics',
  Optimized: 'optimized',
  'Add More': 'addMore',
};

function resumeSectionDisplayTitle(title: string, t: TFunction): string {
  const slug = RESUME_SECTION_TITLE_KEYS[title];
  return slug ? t(`views.accountSettings.resume.addSections.${slug}`, title) : title;
}

function resumeHealthDisplayLabel(label: string, t: TFunction): string {
  const slug = HEALTH_LABEL_KEYS[label];
  return slug ? t(`views.accountSettings.resume.health.${slug}`, label) : label;
}

type SectionSaveState = 'idle' | 'pending' | 'saving' | 'saved';

const SectionAutosaveBadge: React.FC<{ state?: SectionSaveState }> = ({ state }) => {
  const { t } = useTranslation();
  if (!state || state === 'idle') return null;
  if (state === 'pending' || state === 'saving') {
    return (
      <span className="t-tag t-muted uppercase shrink-0" style={{ fontFamily: RESUME_FONT }}>
        {t('views.accountSettings.resume.savingSection', 'Saving...')}
      </span>
    );
  }
  return (
    <span className="t-tag t-ok uppercase shrink-0" style={{ fontFamily: RESUME_FONT }}>
      {t('views.accountSettings.resume.autosaved', 'Autosaved')}
    </span>
  );
};

const HEALTH_BADGE_CLASS: Record<SectionHealthTone, string> = {
  strong: 'section-health-badge section-health-badge--strong',
  optimized: 'section-health-badge section-health-badge--optimized',
  warning: 'section-health-badge section-health-badge--warning',
  empty: 'section-health-badge section-health-badge--empty',
};

const SectionHealthBadge: React.FC<{ title: string; content: string }> = ({ title, content }) => {
  const { t } = useTranslation();
  const health = getSectionHealth({ title, content });
  return (
    <span className={HEALTH_BADGE_CLASS[health.tone]} style={{ fontFamily: RESUME_FONT }}>
      {resumeHealthDisplayLabel(health.label, t)}
    </span>
  );
};

const HealthProgressRow: React.FC<{ label: string; value: number; total: number; color: string }> = ({
  label,
  value,
  total,
  color,
}) => {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <span className="t-card-title">
          {label}
        </span>
        <span className="t-card-title t-num" style={{ color }}>
          {value}/{Math.max(total, 1)}
        </span>
      </div>
      <div className="ds-progress">
        <div
          className="ds-progress-fill"
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
    </div>
  );
};

const AiCoachBanner: React.FC<{ tip: string; variant?: 'blue' | 'amber' }> = ({
  tip,
  variant = 'blue',
}) => {
  const { t } = useTranslation();
  return (
  <div
    className={`ai-coach-banner ${variant === 'amber' ? 'ai-coach-banner--amber' : ''}`}
    style={{ fontFamily: RESUME_FONT }}
  >
    <p>
      <span className="font-semibold">{t('views.accountSettings.resume.aiTip', 'AI Tip:')}</span>{' '}
      <span className="font-normal italic">{tip}</span>
    </p>
  </div>
  );
};

type AiOptimizeMode = 'tone' | 'target';

const AiOptimizeToolbar: React.FC<{
  mode: AiOptimizeMode | null;
  onOptimize: (mode: AiOptimizeMode) => void;
  showTarget?: boolean;
}> = ({ mode, onOptimize, showTarget = true }) => {
  const { t } = useTranslation();
  return (
  <div className="flex flex-wrap items-center justify-end gap-2">
    <button
      type="button"
      disabled={mode !== null}
      onClick={() => onOptimize('tone')}
      className="ai-optimize-btn"
      style={{ fontFamily: RESUME_FONT }}
    >
      {mode === 'tone' ? (
        <LoadingSpinner size="xs" showText={false} />
      ) : (
        <Sparkles className="w-3 h-3" />
      )}
      {t('views.accountSettings.resume.improveTone', 'Improve Tone')}
    </button>
    {showTarget && (
      <button
        type="button"
        disabled={mode !== null}
        onClick={() => onOptimize('target')}
        className="ai-optimize-btn"
        style={{ fontFamily: RESUME_FONT }}
      >
        {mode === 'target' ? (
          <LoadingSpinner size="xs" showText={false} />
        ) : (
          <Sparkles className="w-3 h-3" />
        )}
        {t('views.accountSettings.resume.optimizeTargetJob', 'Optimize for Target Job')}
      </button>
    )}
  </div>
  );
};

const SkillsAiPanel: React.FC<{
  skills: string[];
  recommended: string[];
  onChange: (skills: string[]) => void;
}> = ({ skills, recommended, onChange }) => {
  const { t } = useTranslation();
  const [justAdded, setJustAdded] = useState<string | null>(null);

  const removeSkill = (skill: string) => {
    onChange(skills.filter((s) => s !== skill));
  };

  const addSkill = (skill: string) => {
    if (skills.some((s) => s.toLowerCase() === skill.toLowerCase())) return;
    onChange([...skills, skill]);
    setJustAdded(skill);
    window.setTimeout(() => setJustAdded(null), 400);
  };

  return (
    <div className="space-y-4">
      <div>
        <p
          className="t-section mb-2"
          style={{ fontFamily: RESUME_FONT, color: 'var(--ds-text-muted)' }}
        >
          {t('views.accountSettings.resume.activeSkills', 'Active Skills')}
        </p>
        {skills.length === 0 ? (
          <p className="t-meta leading-[1.4]" style={{ fontFamily: RESUME_FONT }}>
            {t('views.accountSettings.resume.noSkillsYet', 'No skills yet. Add from recommendations or upload a resume.')}
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {skills.map((skill) => (
              <span
                key={skill}
                className={`skill-pill skill-pill--active ${
                  justAdded === skill ? 'skill-pill--just-added' : ''
                }`}
                style={{ fontFamily: RESUME_FONT }}
              >
                {skill}
                <button
                  type="button"
                  onClick={() => removeSkill(skill)}
                  className="p-0.5 rounded-full transition-colors"
                  style={{ color: 'var(--ds-text-muted)' }}
                  aria-label={t('views.accountSettings.resume.removeSkillAria', { defaultValue: 'Remove {{skill}}', skill })}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {recommended.length > 0 && (
        <div>
          <p
            className="t-label t-brand mb-2 flex items-center gap-1"
            style={{ fontFamily: RESUME_FONT }}
          >
            <Sparkles className="w-3 h-3" />
            {t('views.accountSettings.resume.recommendedByAi', 'Recommended by AI')}
          </p>
          <div className="flex flex-wrap gap-2">
            {recommended.map((skill) => (
              <button
                key={skill}
                type="button"
                onClick={() => addSkill(skill)}
                className="skill-pill skill-pill--recommended"
                style={{ fontFamily: RESUME_FONT }}
              >
                + {skill}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Public props ──────────────────────────────────────────────────────────────
export interface ProfileResumeFieldsProps {
  idPrefix: string;
  draft: OnboardingDraft;
  errors: OnboardingErrors;
  onPatch: (patch: Partial<OnboardingDraft>) => void;
}

function isWorkExperienceSection(title: string): boolean {
  const lower = title.toLowerCase();
  return lower.includes('experience') || lower.includes('work') || lower.includes('employment');
}

/** Human-readable matrix/accordion preview — never show ___WORK_JSON___ blobs. */
function formatSectionPreview(
  section: Pick<ResumeSection, 'title' | 'content' | 'preview'>,
  emptyLabel = 'No content yet',
): string {
  const empty = emptyLabel;
  if (!section.content?.trim() && !section.preview?.trim()) return empty;

  if (isWorkExperienceSection(section.title)) {
    if (isWorkExperienceJsonContent(section.content)) {
      const entries = parseWorkExperienceContent(section.content);
      const first = entries[0];
      const role = [first?.jobTitle, first?.company].filter(Boolean).join(' at ');
      if (role) {
        const more = entries.length > 1 ? ` · +${entries.length - 1} more` : '';
        return `${role}${more}`.slice(0, 140);
      }
      const display = getWorkExperienceDisplayContent(section.content);
      const line = display.split('\n').map((l) => l.trim()).find(Boolean);
      if (line) return line.slice(0, 140);
    }
    if (section.preview?.trim() && !section.preview.includes('___WORK_JSON')) {
      return section.preview.slice(0, 140);
    }
  }

  const raw =
    (section.preview?.trim() && !section.preview.includes('___WORK_JSON')
      ? section.preview
      : '') ||
    getWorkExperienceDisplayContent(section.content) ||
    section.content
      .split('\n')
      .map((line) => line.trim())
      .find(Boolean) ||
    '';

  const cleaned = raw.replace(/___WORK_JSON___[\s\S]*/g, '').trim();
  return cleaned.slice(0, 140) || empty;
}

// ── Section icon helper (mirrors ParsedResumeStep.sectionIcon) ────────────────
function sectionIcon(title: string): React.ReactNode {
  const lower = title.toLowerCase();
  const cls = 'w-4 h-4 shrink-0';
  if (lower.includes('personal') || lower.includes('contact') || lower.includes('reference'))
    return <User className={cls} />;
  if (lower.includes('experience') || lower.includes('work') || lower.includes('employment'))
    return <Briefcase className={cls} />;
  if (lower.includes('education')) return <GraduationCap className={cls} />;
  if (lower.includes('award') || lower.includes('achievement') || lower.includes('honor'))
    return <Award className={cls} />;
  if (lower.includes('certif') || lower.includes('publication'))
    return <BookOpen className={cls} />;
  if (
    lower.includes('skill') ||
    lower.includes('language') ||
    lower.includes('framework') ||
    lower.includes('goal')
  )
    return <Code className={cls} />;
  return <FileText className={cls} />;
}

// ── Draft → ResumeSection[] conversion ────────────────────────────────────────
function draftToSections(draft: OnboardingDraft): ResumeSection[] {
  const out: ResumeSection[] = [];

  if (draft.professionalSummary?.trim()) {
    out.push({
      title: 'Professional Summary',
      content: draft.professionalSummary,
      preview: draft.professionalSummary.slice(0, 120),
    });
  }

  if (draft.experiences?.length > 0) {
    const entries = draft.experiences.map((exp, i) => ({
      id: `work-${i}`,
      company: exp.company || '',
      jobTitle: exp.title || '',
      city: exp.location || '',
      country: '',
      fromDate: exp.startDate || '',
      toDate: exp.endDate || '',
      currentlyHere: exp.current || false,
      description: (exp.bullets || []).join('\n'),
    }));
    out.push({
      title: 'Work Experience',
      content: serializeWorkExperienceContent(entries),
      preview: draft.experiences[0]?.title || '',
    });
  }

  if (draft.education?.length > 0) {
    const content = draft.education
      .map((e) =>
        [e.degree, e.location, e.institution, e.year, e.gpa ? `GPA: ${e.gpa}` : '']
          .filter(Boolean)
          .join(' · '),
      )
      .join('\n\n');
    out.push({ title: 'Education', content, preview: content.slice(0, 120) });
  }

  if (draft.skills?.length > 0) {
    const content = draft.skills.join('\n');
    out.push({ title: 'Skills', content, preview: draft.skills.slice(0, 5).join(', ') });
  }

  if (draft.certifications?.length > 0) {
    const content = draft.certifications
      .map((c) => [c.name, c.issuer, c.date].filter(Boolean).join(' · '))
      .join('\n');
    out.push({ title: 'Certifications', content, preview: content.slice(0, 120) });
  }

  if (draft.languages?.length > 0) {
    const content = draft.languages
      .map((l) => `${l.name}${l.proficiency ? ` (${l.proficiency})` : ''}`)
      .join('\n');
    out.push({ title: 'Languages', content, preview: content.slice(0, 120) });
  }

  if (draft.projects?.length > 0) {
    const content = draft.projects
      .map((p) =>
        [
          p.name,
          p.description,
          p.technologies?.length ? `Technologies: ${p.technologies.join(', ')}` : '',
        ]
          .filter(Boolean)
          .join('\n'),
      )
      .join('\n\n');
    out.push({ title: 'Projects', content, preview: content.slice(0, 120) });
  }

  return out;
}

// ── Skill line parser — strips "Category Label: " prefixes ───────────────────
// Handles content like:
//   "Languages & Frameworks: Java, Python, Go"   → ["Java","Python","Go"]
//   "Kubernetes, Docker"                          → ["Kubernetes","Docker"]
function parseSkillLines(content: string): string[] {
  const skills: string[] = [];
  for (const line of content.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const colonIdx = trimmed.indexOf(':');
    // "Category: skill1, skill2" — take only the part after the colon
    const skillPart =
      colonIdx > 0 ? trimmed.slice(colonIdx + 1).replace(/^:?\s*/, '') : trimmed;
    for (const chunk of skillPart.split(',')) {
      const s = chunk.trim();
      if (s.length > 1 && s.length < 60) skills.push(s);
    }
  }
  return [...new Set(skills)];
}

/**
 * Normalise an existing skills array that may contain "Category: Skill" entries
 * left over from earlier resume parses.  Safe to call on already-clean arrays.
 */
export function normalizeSkillsArray(skills: string[]): string[] {
  return parseSkillLines(skills.join('\n'));
}

// ── ResumeSection change → OnboardingDraft partial patch ─────────────────────
function sectionToDraftPatch(section: ResumeSection): Partial<OnboardingDraft> {
  const lower = section.title.toLowerCase();
  const content = section.content || '';

  if (
    lower.includes('summary') ||
    lower.includes('profile') ||
    lower.includes('objective') ||
    lower.includes('professional')
  ) {
    return { professionalSummary: content };
  }

  if (lower.includes('experience') || lower.includes('work') || lower.includes('employment')) {
    const entries = parseWorkExperienceContent(content);
    const experiences: WorkExperience[] = entries.map((e) => ({
      title: e.jobTitle || '',
      company: e.company || '',
      location: [e.city, e.country].filter(Boolean).join(', '),
      startDate: e.fromDate || '',
      endDate: e.toDate || undefined,
      current: e.currentlyHere || false,
      bullets: (e.description || '').split('\n').filter((l) => l.trim()),
    }));
    return { experiences };
  }

  if (lower.includes('education')) {
    const blocks = content.split(/\n\n+/).filter(Boolean);
    const education: Education[] = blocks
      .map((block) => {
        const parts = block
          .split('\n')[0]
          ?.split(' · ')
          .map((p) => p.trim()) ?? [];
        return {
          degree: parts[0] || '',
          location: parts[1] || '',
          institution: parts[2] || parts[1] || '',
          year: parts[3] || '',
          gpa: parts.find((p) => p.startsWith('GPA:'))?.replace('GPA:', '').trim() || '',
        };
      })
      .filter((e) => e.degree || e.institution);
    return { education };
  }

  if (lower.includes('skill')) {
    return { skills: parseSkillLines(content) };
  }

  if (lower.includes('certif')) {
    const lines = content.split('\n').filter(Boolean);
    const certifications: Certification[] = lines
      .map((line) => {
        const parts = line.split(' · ');
        return { name: parts[0] || line, issuer: parts[1] || '', date: parts[2] || '' };
      })
      .filter((c) => c.name);
    return { certifications };
  }

  if (lower.includes('language')) {
    const lines = content.split('\n').filter(Boolean);
    const languages: Language[] = lines
      .map((line) => {
        const m = line.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
        return m
          ? { name: m[1].trim(), proficiency: m[2].trim() as Language['proficiency'] }
          : { name: line.trim(), proficiency: 'Conversational' as Language['proficiency'] };
      })
      .filter((l) => l.name);
    return { languages };
  }

  if (lower.includes('project')) {
    const blocks = content.split(/\n\n+/).filter(Boolean);
    const projects: Project[] = blocks
      .map((block) => {
        const lines = block.split('\n').map((l) => l.trim()).filter(Boolean);
        const techLine = lines.find((l) => l.startsWith('Technologies:'));
        const technologies = techLine
          ? techLine.replace('Technologies:', '').split(',').map((t) => t.trim())
          : [];
        const descLines = lines.filter((l) => !l.startsWith('Technologies:'));
        return {
          name: descLines[0] || '',
          description: descLines.slice(1).join('\n'),
          technologies: technologies.length ? technologies : undefined,
        };
      })
      .filter((p) => p.name);
    return { projects };
  }

  return {};
}

// ── Addable section types ─────────────────────────────────────────────────────
const ADDABLE_SECTIONS = [
  'Work Experience',
  'Education',
  'Skills',
  'Certifications',
  'Languages',
  'Projects',
  'Awards',
  'Professional Summary',
  'Goal',
  'Achievements',
  'Publications',
  'Hobbies',
  'References',
];

// ── Contact info extractor (used after upload) ────────────────────────────────
function extractContactInfo(rawText: string, bucketedSections: Array<{ title: string; content: string }>) {
  const getSectionContent = (...kws: string[]) => {
    for (const kw of kws) {
      const s = bucketedSections.find((sec) => sec.title.toLowerCase().includes(kw.toLowerCase()));
      if (s?.content?.trim()) return s.content.trim();
    }
    return '';
  };

  const contactContent = getSectionContent('Personal Information', 'Contact');
  const headerText = contactContent || rawText.slice(0, 700);
  const lines = headerText.split('\n').map((l) => l.trim()).filter(Boolean);

  const info: { name?: string; headline?: string; email?: string; phone?: string; location?: string; linkedin?: string } = {};

  if (lines[0] && lines[0].length < 80 && !lines[0].includes('@') && !/^\+?\d/.test(lines[0]))
    info.name = lines[0];

  const line2 = lines[1] ?? '';
  if (
    line2 &&
    line2.length < 120 &&
    !line2.includes('@') &&
    !/^\+?\d/.test(line2) &&
    !/mobile|email|phone/i.test(line2)
  )
    info.headline = line2.replace(/^[•·\-]\s*/, '').trim();

  const fullText = headerText + '\n' + rawText.slice(0, 900);
  const emailM = fullText.match(/(?:email\s*[:\-]\s*)?([a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,})/i);
  if (emailM) info.email = emailM[1];

  const phoneM =
    fullText.match(/(?:mobile|phone|tel|ph)\s*[:\-]\s*(\+?[\d\s\(\)\-\.]{7,17})/i) ??
    fullText.match(/(?:^|[•·\s])(\+?[\d\s\(\)\-\.]{7,17})(?=[•·\s]|$)/m);
  if (phoneM) info.phone = (phoneM[1] ?? '').trim();

  const liM = fullText.match(/https?:\/\/(?:www\.)?linkedin\.com\/in\/[^\s,•\)]+/i);
  if (liM) info.linkedin = liM[0].replace(/[,.\)]$/, '');

  return info;
}

// ── Main component ────────────────────────────────────────────────────────────
const ProfileResumeFields: React.FC<ProfileResumeFieldsProps> = ({
  draft,
  onPatch,
}) => {
  const { t, i18n } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const [sections, setSections] = useState<ResumeSection[]>(() => draftToSections(draft));
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [showAddSection, setShowAddSection] = useState(false);
  const [sectionSaveState, setSectionSaveState] = useState<Record<number, SectionSaveState>>({});
  const [optimizing, setOptimizing] = useState<{ index: number; mode: AiOptimizeMode } | null>(null);
  const [optimizeError, setOptimizeError] = useState<string | null>(null);

  const sectionRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;
  const debounceRefs = useRef<Record<number, ReturnType<typeof setTimeout>>>({});

  useEffect(() => () => {
    Object.values(debounceRefs.current).forEach(clearTimeout);
  }, []);

  const scheduleAutosave = useCallback((index: number) => {
    setSectionSaveState(prev => ({ ...prev, [index]: 'pending' }));
    if (debounceRefs.current[index]) clearTimeout(debounceRefs.current[index]);
    debounceRefs.current[index] = setTimeout(() => {
      const section = sectionsRef.current[index];
      if (!section) return;
      setSectionSaveState(prev => ({ ...prev, [index]: 'saving' }));
      const patch = sectionToDraftPatch(section);
      if (Object.keys(patch).length > 0) onPatch(patch);
      setSectionSaveState(prev => ({ ...prev, [index]: 'saved' }));
      setTimeout(() => {
        setSectionSaveState(prev => ({ ...prev, [index]: 'idle' }));
      }, 2500);
    }, 1000);
  }, [onPatch]);

  // Stable proxy for "draft has resume data": sum of counts of key arrays.
  // Changes from 0 to N when the profile loads from Supabase after mount.
  const draftDataSize = useMemo(
    () =>
      (draft.experiences?.length ?? 0) +
      (draft.skills?.length ?? 0) +
      (draft.education?.length ?? 0) +
      (draft.professionalSummary ? 1 : 0),
    [draft.experiences?.length, draft.skills?.length, draft.education?.length, draft.professionalSummary],
  );

  // Populate sections once (and only once) after draft data arrives.
  // Uses a ref flag so user edits made AFTER load are never overwritten.
  const hasInitializedRef = useRef(false);
  useEffect(() => {
    if (hasInitializedRef.current) return;
    const derivedSections = draftToSections(draft);
    if (derivedSections.length > 0) {
      hasInitializedRef.current = true;
      setSections(derivedSections);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftDataSize]);

  // Scroll + expand when floating completeness widget navigates to a resume section
  useEffect(() => {
    const h = (e: Event) => {
      const { sectionKey } = (e as CustomEvent<ResumeSectionFocusDetail>).detail ?? {};
      if (!sectionKey) return;
      const idx = findSectionIndexForKey(sectionsRef.current, sectionKey);
      if (idx < 0) return;
      setExpandedIndex(idx);
      window.setTimeout(() => {
        sectionRefs.current[idx]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 120);
    };
    window.addEventListener(RESUME_SECTION_FOCUS_EVENT, h as EventListener);
    return () => window.removeEventListener(RESUME_SECTION_FOCUS_EVENT, h as EventListener);
  }, []);

  const updateSectionContent = useCallback(
    (index: number, content: string) => {
      setSections((prev) => {
        const next = prev.map((s, i) =>
          i === index
            ? { ...s, content, preview: formatSectionPreview({ ...s, content }, t('views.accountSettings.resume.noContentYet', 'No content yet')) }
            : s,
        );
        sectionsRef.current = next;
        return next;
      });
      scheduleAutosave(index);
    },
    [scheduleAutosave, t],
  );

  const handleAiOptimize = useCallback(
    async (index: number, mode: AiOptimizeMode) => {
      const section = sectionsRef.current[index];
      if (!section?.content?.trim()) {
        setOptimizeError(t('views.accountSettings.resume.errorAddContentFirst', 'Add some content first, then optimize with AI.'));
        window.setTimeout(() => setOptimizeError(null), 3500);
        return;
      }

      setOptimizing({ index, mode });
      setOptimizeError(null);

      const targetHint =
        mode === 'target' && draft.currentRole?.trim()
          ? ` Tailor for a ${draft.currentRole} role.`
          : mode === 'target'
            ? ' Tailor for the candidate\'s target job type.'
            : '';

      const instruction =
        mode === 'tone'
          ? 'Improve tone, clarity, and impact. Use strong action verbs.'
          : `Optimize for ATS and recruiter scans.${targetHint} Include quantifiable metrics where possible.`;

      try {
        const { data, error } = await supabase.functions.invoke('ai-companion', {
          body: {
            message: `Rewrite the following "${section.title}" resume section. ${instruction} Return ONLY the improved content with no explanation, quotes, or markdown fences.\n\n${section.content}`,
          },
        });
        if (error) throw error;
        const improved: string =
          data?.reply ?? data?.content ?? data?.message ?? data?.text ?? '';
        if (improved.trim()) {
          updateSectionContent(index, improved.trim());
        }
      } catch {
        setOptimizeError(t('views.accountSettings.resume.aiOptimizeFailed', 'AI optimization failed. Try again.'));
        window.setTimeout(() => setOptimizeError(null), 4000);
      } finally {
        setOptimizing(null);
      }
    },
    [draft.currentRole, updateSectionContent, t],
  );

  const updateSectionTitle = useCallback((index: number, title: string) => {
    setSections((prev) => {
      const next = prev.map((s, i) => (i === index ? { ...s, title } : s));
      sectionsRef.current = next;
      return next;
    });
    scheduleAutosave(index);
  }, [scheduleAutosave]);

  const removeSection = useCallback((index: number) => {
    setSections((prev) => prev.filter((_, i) => i !== index));
    setExpandedIndex((prev) =>
      prev === index ? null : prev !== null && prev > index ? prev - 1 : prev,
    );
  }, []);

  const addSection = useCallback(
    (title: string) => {
      setSections((prev) => {
        const next = [...prev, { title, content: '', preview: '' }];
        sectionsRef.current = next;
        setExpandedIndex(next.length - 1);
        return next;
      });
      setShowAddSection(false);
    },
    [],
  );

  const focusSection = useCallback((index: number) => {
    setExpandedIndex(index);
    window.setTimeout(() => {
      sectionRefs.current[index]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 80);
  }, []);

  const sectionHealthSummary = useMemo(() => {
    const health = sections.map((section) => getSectionHealth(section));
    const polished = health.filter((h) => h.tone === 'strong' || h.tone === 'optimized').length;
    const needsWork = health.filter((h) => h.tone === 'warning' || h.tone === 'empty').length;
    const skillsSection = sections.find((section) => section.title.toLowerCase().includes('skill'));
    const skillCount = skillsSection ? parseSkillLines(skillsSection.content).length : 0;

    return {
      total: sections.length,
      polished,
      needsWork,
      skillCount,
    };
  }, [sections]);

  // ── File upload ─────────────────────────────────────────────────────────────
  const handleFileUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (file.size > 5 * 1024 * 1024) {
        setUploadError(t('onboarding.step5.errorFileTooLarge', 'File size must be less than 5MB.'));
        return;
      }
      const validTypes = [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain',
      ];
      if (!validTypes.includes(file.type) && !file.name.match(/\.(pdf|docx?|txt)$/i)) {
        setUploadError(t('onboarding.step5.errorInvalidType', 'Please upload a PDF, DOCX, or TXT file.'));
        return;
      }

      setIsUploading(true);
      setUploadError(null);
      setUploadSuccess(false);

      try {
        const parsed = await langchainResumeParser.parseResume(file);

        if (!parsed.rawText || parsed.rawText.length < 50) {
          throw new Error('Could not extract text. Please try a different format.');
        }

        const rawText = parsed.rawText;
        const bucketedSections = (parsed.sections || []) as Array<{ title: string; content: string }>;

        // Build ResumeSection[] from parser output, filtering out empty / contact-only sections
        const newSections: ResumeSection[] = bucketedSections
          .filter((s) => {
            const lower = s.title.toLowerCase();
            return (
              s.content?.trim() &&
              !lower.includes('contact') &&
              !lower.includes('personal information')
            );
          })
          .map((s) => ({ title: s.title, content: s.content, preview: s.content.slice(0, 120) }));

        setSections(newSections.length > 0 ? newSections : draftToSections(draft));

        // Extract contact info for other draft fields
        const contact = extractContactInfo(rawText, bucketedSections);
        const patch: Partial<OnboardingDraft> = {
          resumeRawText: rawText,
          resumeFileName: file.name,
          resumeSource: 'upload',
          resumeUpdatedAt: new Date().toISOString(),
        };
        if (contact.name) patch.displayName = contact.name;
        if (contact.headline) patch.headline = contact.headline;
        if (contact.linkedin) patch.linkedinUrl = contact.linkedin;

        // Also sync all sections to structured fields
        for (const sec of newSections) {
          const secPatch = sectionToDraftPatch(sec);
          Object.assign(patch, secPatch);
        }

        onPatch(patch);
        setUploadSuccess(true);
        setTimeout(() => setUploadSuccess(false), 4000);
      } catch (err) {
        setUploadError((err as Error).message || t('onboarding.step5.errorParseFailed', 'Failed to parse resume'));
      } finally {
        setIsUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
      }
    },
    [draft, onPatch, t],
  );

  // ── Render ──────────────────────────────────────────────────────────────────
  const emptyPreview = t('views.accountSettings.resume.noContentYet', 'No content yet');
  const dateLocaleMap: Record<string, string> = { en: 'en-US', es: 'es-ES', pt: 'pt-BR', ru: 'ru-RU' };
  const localeForDate = dateLocaleMap[i18n.language?.split('-')[0] || ''] ?? i18n.language ?? 'en-US';

  return (
    <div key={i18n.language} className="resume-workspace ds-resume-workspace p-4 sm:p-5" style={{ fontFamily: RESUME_FONT }}>
      <aside className="resume-coach-sidebar">
        <div className="resume-coach-card">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="dashboard-kicker">{t('views.accountSettings.resume.optimizationHealth', 'Optimization Health')}</p>
              <p className="mt-1 t-hero leading-none">
                {sectionHealthSummary.polished}/{Math.max(sectionHealthSummary.total, 1)}
              </p>
              <p className="mt-1 t-meta leading-[1.45]">
                {t('views.accountSettings.resume.sectionsPolished', 'sections polished')}
              </p>
            </div>
            <div className="resume-coach-spark">
              <Sparkles className="h-5 w-5" />
            </div>
          </div>

          <div className="mt-5 space-y-4">
            <HealthProgressRow
              label={t('views.accountSettings.resume.polishedSections', 'Polished sections')}
              value={sectionHealthSummary.polished}
              total={sectionHealthSummary.total}
              color="var(--ds-brand)"
            />
            <HealthProgressRow
              label={t('views.accountSettings.resume.needsWork', 'Needs work')}
              value={sectionHealthSummary.needsWork}
              total={sectionHealthSummary.total}
              color="var(--ds-warn)"
            />
          </div>

          <div className="mt-5 grid grid-cols-2 gap-2">
            <div className="resume-sidebar-stat">
              <p>{sectionHealthSummary.total}</p>
              <span>{t('views.accountSettings.resume.sections', 'Sections')}</span>
            </div>
            <div className="resume-sidebar-stat">
              <p>{sectionHealthSummary.skillCount}</p>
              <span>{t('views.accountSettings.resume.skills', 'Skills')}</span>
            </div>
          </div>

          <div className="mt-5 ds-recessed-warn">
            <p className="t-label">
              {t('views.accountSettings.resume.coachSignal', 'Coach signal')}
            </p>
            <p className="mt-1 t-body leading-[1.5]">
              {sectionHealthSummary.needsWork > 0
                ? t('views.accountSettings.resume.coachNeedsWork', 'Prioritize sections marked Missing Metrics or Too Short before saving.')
                : t('views.accountSettings.resume.coachReady', 'Your resume sections are ready for a final review pass.')}
            </p>
          </div>
        </div>
      </aside>

      <main className="min-w-0 space-y-5">
        <div className="space-y-3">
          <div>
            <p className="t-label t-brand">
              {t('views.accountSettings.resume.workspaceKicker', 'AI Resume Workspace')}
            </p>
            <h3 className="mt-1 t-h3">
              {t('views.accountSettings.resume.workspaceTitle', 'Tune each section with live coach signals')}
            </h3>
            <p className="mt-1.5 max-w-2xl t-meta leading-[1.5]">
              {t('views.accountSettings.resume.workspaceDesc', 'Upload once, then review section health, improve copy, and add AI-recommended keywords from a single workspace.')}
            </p>
          </div>

          {/* ── Upload dropzone (full width, above accordions) ──────────────────── */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.docx,.doc,.txt"
        className="hidden"
        onChange={handleFileUpload}
      />
      <button
        type="button"
        disabled={isUploading}
        onClick={() => fileInputRef.current?.click()}
        className="upload-dropzone resume-upload-card w-full rounded-xl disabled:cursor-not-allowed"
        aria-label={t('views.accountSettings.resume.uploadAria', 'Upload resume file')}
      >
        <div className="flex items-center gap-3 text-left">
          <span className="resume-upload-icon">
            {isUploading ? (
              <LoadingSpinner size="xs" showText={false} />
            ) : (
              <Upload className="w-4 h-4" />
            )}
          </span>
          <span className="min-w-0">
            <span className="block t-card-title leading-[1.25]">
              {isUploading
                ? t('onboarding.step5.parsing', 'Parsing your resume...')
                : t('views.accountSettings.resume.uploadOrReplace', 'Upload or replace your resume')}
            </span>
            <span className="mt-1 block t-meta leading-[1.45]">
              {t('views.accountSettings.resume.uploadHint', 'PDF, DOCX, or TXT. Max 5 MB. Auto-populates all sections below.')}
            </span>
          </span>
        </div>
      </button>
        </div>

      {(draft.resumeUpdatedAt || draft.resumeSource) && !uploadSuccess && (
        <div className="flex items-center justify-center gap-1.5 t-meta t-muted -mt-2">
          <CheckCircle2 className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--ds-ok)' }} />
          <span>
            {draft.resumeFileName
              ? <>{t('views.accountSettings.resume.fileLabel', 'File:')} <span className="font-medium">{draft.resumeFileName}</span></>
              : t('views.accountSettings.resume.resumeDataLoaded', 'Resume data loaded')}
            {draft.resumeUpdatedAt && (
              <> · {t('views.accountSettings.resume.lastUpdated', 'Last updated')}{' '}
                {new Date(draft.resumeUpdatedAt).toLocaleDateString(localeForDate, {
                  month: 'short', day: 'numeric', year: 'numeric',
                })}
              </>
            )}
          </span>
        </div>
      )}

      {uploadError && (
        <div className="flex items-center gap-2 t-meta t-danger -mt-2">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" />
          {uploadError}
        </div>
      )}
      {uploadSuccess && (
        <div className="flex items-center gap-2 t-meta t-ok -mt-2">
          <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
          {t('views.accountSettings.resume.uploadSuccess', 'Resume parsed — sections below updated.')}
        </div>
      )}

      <p className="t-tag t-muted text-center -mt-2">
        <span className="font-medium">{t('onboarding.step5.linkedinTipTitle', 'Have a LinkedIn profile?')}</span>{' '}
        {t('onboarding.step5.linkedinTipBody', 'Export your LinkedIn profile as PDF:')}{' '}
        {t('onboarding.step5.linkedinTipSteps', 'Profile → More → Save to PDF')},{' '}
        {t('onboarding.step5.linkedinTipSuffix', '. Then upload it here to auto-fill your experience and education.')}
      </p>

      {optimizeError && (
        <div className="flex items-center gap-2 t-meta t-danger">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" />
          {optimizeError}
        </div>
      )}

      {sections.length > 0 && (
        <div className="resume-section-map">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <p className="dashboard-kicker">{t('views.accountSettings.resume.sectionMatrix', 'Section Matrix')}</p>
              <p className="mt-1 t-meta leading-[1.45]">
                {t('views.accountSettings.resume.sectionMatrixDesc', 'Jump into the section that needs the next polish pass.')}
              </p>
            </div>
            <span className="ds-chip">
              {sectionHealthSummary.needsWork > 0
                ? t('views.accountSettings.resume.toImprove', {
                    defaultValue: '{{count}} to improve',
                    count: sectionHealthSummary.needsWork,
                  })
                : t('views.accountSettings.resume.readyForReview', 'Ready for review')}
            </span>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            {sections.map((section, i) => {
              const health = getSectionHealth(section);
              const preview = formatSectionPreview(section, emptyPreview);

              return (
                <button
                  key={`${section.title}-tile-${i}`}
                  type="button"
                  onClick={() => focusSection(i)}
                  className={`resume-section-tile ${
                    expandedIndex === i ? 'resume-section-tile--active' : ''
                  }`}
                >
                  <span className="resume-section-tile__icon">{sectionIcon(section.title)}</span>
                  <span className="min-w-0 flex-1 text-left">
                    <span className="flex items-start justify-between gap-2">
                      <span className="resume-section-tile__title">
                        {resumeSectionDisplayTitle(section.title, t)}
                      </span>
                      <span className={HEALTH_BADGE_CLASS[health.tone]}>{resumeHealthDisplayLabel(health.label, t)}</span>
                    </span>
                    <span className="resume-section-tile__meta">
                      {preview}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Section accordion list ───────────────────────────────────────────── */}
      <div className="space-y-2">
        {sections.map((section, i) => {
          const isExpanded = expandedIndex === i;
          const isWorkExp =
            section.title.toLowerCase().includes('experience') ||
            section.title.toLowerCase().includes('work') ||
            section.title.toLowerCase().includes('employment');
          const isSkills = section.title.toLowerCase().includes('skill');
          const aiTip = getSectionAiTip(section);
          const activeSkills = parseSkillLines(section.content);
          const recommendedSkills = isSkills
            ? getRecommendedSkills(activeSkills, draft)
            : [];
          const isOptimizingSection = optimizing?.index === i;
          const optimizingMode = isOptimizingSection ? optimizing?.mode ?? null : null;

          return (
            <div
              key={`${section.title}-${i}`}
              ref={(el) => { sectionRefs.current[i] = el; }}
              data-resume-section={section.title}
              className="resume-accordion-card group"
            >
              {/* ── Accordion header ── */}
              <div className="resume-accordion-header">
                <button
                  type="button"
                  onClick={() => setExpandedIndex(isExpanded ? null : i)}
                  className="flex-1 flex items-center gap-3 min-w-0 py-1 px-1 text-left"
                >
                  <div className="resume-accordion-icon">
                    {sectionIcon(section.title)}
                  </div>
                  <span
                    className="resume-accordion-title"
                    style={{ fontFamily: RESUME_FONT }}
                  >
                    {resumeSectionDisplayTitle(section.title, t)}
                  </span>
                  {!isExpanded && (
                    <SectionHealthBadge title={section.title} content={section.content} />
                  )}
                  <SectionAutosaveBadge state={sectionSaveState[i]} />
                  {isExpanded ? (
                    <ChevronUp className="w-4 h-4 shrink-0 ml-1 transition-colors duration-200" style={{ color: 'var(--ds-brand)' }} />
                  ) : (
                    <ChevronDown className="w-4 h-4 shrink-0 ml-1 transition-colors duration-200" style={{ color: 'var(--ds-text-muted)' }} />
                  )}
                </button>

                {/* Delete button — appears on hover */}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); removeSection(i); }}
                  className="ds-btn ds-btn-ghost ds-btn-sm p-1.5 shrink-0 transition-all opacity-0 group-hover:opacity-100 focus:opacity-100"
                  title={t('views.accountSettings.resume.removeSection', 'Remove section')}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              {/* ── Expanded body ── */}
              {isExpanded && (
                <div className="resume-section-body">
                  {isWorkExp ? (
                    <>
                      <div className="resume-editor-toolbar rounded-t-xl">
                        <span className="resume-field-label mb-0">
                          {t('views.accountSettings.resume.structuredEditor', 'Structured Experience Editor')}
                        </span>
                        <AiOptimizeToolbar
                          mode={optimizingMode}
                          onOptimize={(mode) => void handleAiOptimize(i, mode)}
                        />
                      </div>
                      <div className="resume-work-editor-shell relative rounded-xl">
                        {isOptimizingSection && <div className="ai-shimmer-overlay rounded-xl" />}
                        <WorkExperienceCard
                          content={section.content}
                          sectionTitle={section.title}
                          onContentChange={(v) => updateSectionContent(i, v)}
                        />
                      </div>
                      {aiTip && <AiCoachBanner tip={aiTip} variant="amber" />}
                    </>
                  ) : isSkills ? (
                    <div className="resume-inner-panel">
                      <SkillsAiPanel
                        skills={activeSkills}
                        recommended={recommendedSkills}
                        onChange={(next) => updateSectionContent(i, skillsToContent(next))}
                      />
                      {aiTip && <AiCoachBanner tip={aiTip} />}
                    </div>
                  ) : (
                    <div className="resume-inner-panel">
                      <div className="resume-field-group">
                        <label className="resume-field-label">{t('views.accountSettings.resume.sectionTitle', 'Section title')}</label>
                        <input
                          type="text"
                          value={section.title}
                          onChange={(e) => updateSectionTitle(i, e.target.value)}
                          className="resume-field-input"
                          style={{ fontFamily: RESUME_FONT }}
                        />
                      </div>

                      <div className="resume-field-group">
                        <label className="resume-field-label">{t('views.accountSettings.resume.sectionContent', 'Section content')}</label>
                        <p
                          className="resume-field-helper"
                          style={{ fontFamily: RESUME_FONT }}
                        >
                          {t('views.accountSettings.resume.sectionContentHelper', 'Select text, then click Bold/Italic/Strikethrough/Underline. Formatting appears in Preview.')}
                        </p>
                        <div className="rich-editor-wrapper relative">
                          {isOptimizingSection && <div className="ai-shimmer-overlay" />}
                          <div className="resume-editor-toolbar">
                            <RichTextToolbar
                              textareaId={`section-content-${i}`}
                              value={section.content}
                              onChange={(v) => updateSectionContent(i, v)}
                            />
                            <AiOptimizeToolbar
                              mode={optimizingMode}
                              onOptimize={(mode) => void handleAiOptimize(i, mode)}
                              showTarget={
                                section.title.toLowerCase().includes('summary') ||
                                section.title.toLowerCase().includes('objective') ||
                                section.title.toLowerCase().includes('project')
                              }
                            />
                          </div>
                          <MarkdownSectionEditor
                            id={`section-content-${i}`}
                            value={section.content}
                            onChange={(v) => updateSectionContent(i, v)}
                            minHeight="180px"
                            className="resume-markdown-editor focus:ring-0 border-0 rounded-none"
                            placeholder={t('views.accountSettings.resume.sectionContentPlaceholder', 'Start typing or paste content…')}
                          />
                        </div>
                      </div>
                      {aiTip && (
                        <AiCoachBanner
                          tip={aiTip}
                          variant={
                            section.title.toLowerCase().includes('summary') ? 'amber' : 'blue'
                          }
                        />
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Add New Section ──────────────────────────────────────────────────── */}
      <div className="ds-card-flush">
        <button
          type="button"
          onClick={() => setShowAddSection((v) => !v)}
          className="w-full flex items-center gap-2 px-4 py-3 t-section hover:bg-[var(--ds-bg-hover)] transition-colors"
          style={{ fontFamily: RESUME_FONT }}
        >
          <Plus className="w-4 h-4 shrink-0" />
          {t('views.accountSettings.resume.addNewSection', 'Add New Section')}
          <ChevronDown
            className={`w-4 h-4 ml-auto transition-transform ${showAddSection ? 'rotate-180' : ''}`}
            style={{ color: 'var(--ds-text-muted)' }}
          />
        </button>

        {showAddSection && (
          <div className="border-t p-3 grid grid-cols-2 gap-2" style={{ borderColor: 'var(--ds-divider)' }}>
            {ADDABLE_SECTIONS.filter(
              (title) =>
                !sections.some(
                  (s) => s.title.toLowerCase() === title.toLowerCase(),
                ),
            ).map((title) => (
              <button
                key={title}
                type="button"
                onClick={() => addSection(title)}
                className="add-section-chip gap-2.5 w-full"
                style={{ fontFamily: RESUME_FONT }}
              >
                <span className="add-section-chip__icon">
                  {sectionIcon(title)}
                </span>
                <span className="add-section-chip__label">{resumeSectionDisplayTitle(title, t)}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      </main>
    </div>
  );
};

export default ProfileResumeFields;
